import { NavigateFunction } from 'react-router-dom';
import { FormInstance, message } from 'antd';
import { isEmpty } from 'lodash';

import { initOfferSuccessAction, IOfferStore } from 'containers/Offers/Offer/store';

import { getDateForPayload, getNestedFormChanges } from 'utils';

import { Links } from 'links';

import { IdWithOrderDto, OfferCreate, OfferDetails, OfferUpdate } from 'codegen';
import { IDataResponse, RecursiveRequired, TRequestCallback } from 'types';

export const handleInitOfferDetailsSuccess = (
  { data }: IDataResponse<OfferDetails>,
) => {
  initOfferSuccessAction(data);
};

export const handleSuccessPatchOfferDetails = (
  { data }: IDataResponse<OfferDetails>,
) => {
  initOfferSuccessAction(data);
  message.success('Offer successfully updated!');
};

export const handleSuccessPostOfferDetails = (
  navigate: NavigateFunction,
) => (
  { data }: IDataResponse<OfferDetails>,
) => {
  navigate(`${Links.Offers}/${data.id}`);
};

const getRetailersForPayload = (retailers?: number | number[]) => {
  if (Array.isArray(retailers)) {
    return retailers.map((id, index) => ({ id, order: index + 1 }));
  }

  if (retailers && typeof retailers === 'number') {
    return [{ id: retailers, order: 1 }];
  }

  return undefined;
};

export const handleOfferDetailsPatchRequest = (
  form: FormInstance<RecursiveRequired<IOfferStore['initialValues']>>,
  initialValues: IOfferStore['initialValues'],
  patchOfferDetails: TRequestCallback<OfferUpdate>,
) => () => {
  const {
    enValues,
    frValues,
    zoneValue,
    retailersValue,
    genericValues,
    milesConfigurationValues,
  } = getNestedFormChanges(initialValues, form);

  const { displayRetailers: retailer, ...restRetValues } = retailersValue;

  const enLocale = !isEmpty(enValues) ? [{ locale: 'en-CA', ...enValues }] : [];
  const frLocale = !isEmpty(frValues) ? [{ locale: 'fr-CA', ...frValues }] : [];
  const displayRetailers = getRetailersForPayload(retailer);

  patchOfferDetails({
    payload: {
      displayRetailers,
      ...zoneValue,
      ...genericValues,
      ...restRetValues,
      ...milesConfigurationValues,
      ...((enLocale.length || frLocale.length) && { translations: [...enLocale, ...frLocale] }),
      ...(genericValues?.effectiveTo && { effectiveTo: getDateForPayload(genericValues.effectiveTo, 'YYYY-MM-DDTHH:mm:ss') }),
      ...(genericValues?.displayFrom && { displayFrom: getDateForPayload(genericValues.displayFrom, 'YYYY-MM-DDTHH:mm:ss') }),
      ...(genericValues?.effectiveFrom && { effectiveFrom: getDateForPayload(genericValues.effectiveFrom, 'YYYY-MM-DDTHH:mm:ss') }),
    },
  });
};

export const handleOfferDetailsPostRequest = (
  form: FormInstance<RecursiveRequired<IOfferStore['initialValues']>>,
  postOfferDetails: TRequestCallback<OfferCreate>,
) => () => {
  const {
    enValues,
    frValues,
    zoneValue,
    retailersValue,
    genericValues,
    milesConfigurationValues,
  } = form.getFieldsValue();

  const { displayRetailers: retailer, ...restRetValues } = retailersValue;

  const enLocale = [{ locale: 'en-CA', ...enValues }];
  const frLocale = [{ locale: 'fr-CA', ...frValues }];
  const displayRetailers = getRetailersForPayload(retailer) as IdWithOrderDto[];

  postOfferDetails({
    payload: {
      displayRetailers,
      ...zoneValue,
      ...genericValues,
      ...restRetValues,
      ...milesConfigurationValues,
      translations: [...enLocale, ...frLocale],
      effectiveTo: getDateForPayload(genericValues.effectiveTo, 'YYYY-MM-DDTHH:mm:ss'),
      displayFrom: getDateForPayload(genericValues.displayFrom, 'YYYY-MM-DDTHH:mm:ss'),
      effectiveFrom: getDateForPayload(genericValues.effectiveFrom, 'YYYY-MM-DDTHH:mm:ss'),
    } as unknown as OfferCreate,
  });
};
